import React, { useState, useEffect, useCallback, useRef } from 'react';

import i18n from 'i18n-js';
import { useQuery } from 'hooks';
import { buildUrl } from 'utils';
import Can from 'components/Can';
import { debounce } from 'lodash';
import { useConfirm } from 'hooks';
import Title from 'components/Title';
import useStyles from './List.styled';
import { Table } from 'components/UI';
import { toQueryString } from 'utils';
import Layout from 'components/Layout';
import { Tooltip } from 'components/UI';
import { useHistory } from 'react-router-dom';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import { SearchField } from 'components/FormFields';
import { truncateKeywordSpaces } from 'utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { PricingActions } from 'state/modules/pricing';
import InputAdornment from '@material-ui/core/InputAdornment';
import { Box, Grid, Paper, Button, Container, IconButton } from '@material-ui/core';
import { AuthActions } from 'state/modules/auth';

const TaxRulesetList = () => {
  const classes = useStyles();
  const urlParams = useQuery();
  const dispatch = useDispatch();
  const history = useHistory();
  const [currentTaxRuleset, setCurrentTaxRuleset] = useState(null);
  const isMounted = useRef(false);
  const abortController = useRef(null);

  const data = useSelector((state) => state.pricing.taxRulesets.data);
  const pagination = useSelector((state) => state.pricing.taxRulesets.pagination);
  const fetchingTaxRulesets = useSelector((state) => state.pricing.fetchingTaxRulesets);
  const taxRulesetsEnabled = useSelector(
    (state) => state.config?.settings?.core?.pricing?.tax_rules_set?.enabled
  );

  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });

  /**
   * Get tax rulesets list
   * @param {object} params Request params
   */
  const fetchTaxRulesets = useCallback(
    (params = {}) => {
      const _params = { ...params };
      if (_params?.keyword) {
        _params.keyword = truncateKeywordSpaces(_params.keyword);
      }
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        PricingActions.fetchTaxRulesets(_params, { signal: abortController?.current?.signal })
      );
    },
    [dispatch]
  );

  const fetchTaxRulesetsDebounced = useCallback(
    debounce((params) => fetchTaxRulesets(params), 1000),
    [fetchTaxRulesets]
  );

  /**
   * Handle delete tax ruleset popup
   */
  const [toggleDeleteTaxRuleset, DeleteTaxRulesetConfirm] = useConfirm({
    title: i18n.t('ruleset.delete_ruleset_title'),
    message: i18n.t('ruleset.delete_ruleset_description'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: useCallback(
      () =>
        dispatch(
          PricingActions.fetchDeleteTaxRuleset(currentTaxRuleset, {
            action: fetchTaxRulesets,
            params: { ...params, page: 1 },
          })
        ),
      [dispatch, currentTaxRuleset, fetchTaxRulesets, params]
    ),
  });

  // TODO Enable option once BE supports delete tax ruleset
  /**
   * Handla delete ruleset
   * @param {object} row Ruleset data
   */
  const handeDelete = (row) => {
    setCurrentTaxRuleset(row.id);
    toggleDeleteTaxRuleset();
  };

  /**
   * Handla edit ruleset
   * @param {object} row Ruleset data
   */
  const handleEdit = (row) => {
    dispatch(PricingActions.resetCurrentRuleset());
    history.push(buildUrl(`/tax-rulesets/${row?.id}/update`));
  };

  /**
   * Handle clone ruleset
   * @param {object} row Ruleset data
   */
  const handleClone = (row) => {
    dispatch(PricingActions.resetCurrentTaxRuleset());
    history.push(buildUrl(`/tax-rulesets/${row?.id}/clone`));
  };

  /**
   * Table columns
   * Show or hide columns with omit key
   */
  const columns = [
    {
      name: i18n.t('common.id'),
      selector: 'id',
      grow: 0,
      minWidth: '4rem',
    },
    {
      name: i18n.t('tax_ruleset.ruleset_name'),
      selector: 'name',
      grow: 2,
    },
    {
      name: i18n.t('common.description'),
      selector: 'description',
      cell: (row) => row?.description || '--',
      grow: 4,
    },
    {
      name: i18n.t('common.actions'),
      selector: 'action',
      cell: (row) => {
        return (
          <Box display="flex" justifyContent="center">
            <Can I="update" a="tax_rules_set">
              <Tooltip title={i18n.t('tax_ruleset.edit_tax_ruleset')}>
                <IconButton aria-label="edit" onClick={() => handleEdit(row)}>
                  <EditIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Can>
            <Can I="create" a="tax_rules_set">
              <Tooltip title={i18n.t('tax_ruleset.clone_tax_ruleset')}>
                <IconButton aria-label="clone" onClick={() => handleClone(row)}>
                  <FileCopyIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Can>
            <Can I="delete" a="tax_rules_set">
              <Tooltip title={i18n.t('tax_ruleset.delete_tax_ruleset')}>
                <IconButton aria-label="delete" onClick={() => handeDelete(row)}>
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Can>
          </Box>
        );
      },
      minWidth: '9rem',
      grow: 0,
      center: true,
      button: true,
    },
  ];

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchTaxRulesetsDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchTaxRulesetsDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    if (!taxRulesetsEnabled) {
      return dispatch(AuthActions.unauthorized());
    }
    fetchTaxRulesets(params);
  }, []);

  const handleParams = (params) => {
    const data = { ...params };
    setParams({ ...data });
    const _params = toQueryString(data);
    history.replace(buildUrl(`/tax-rulesets${_params}`));
  };

  /**
   * Handle page change on table
   * @param {number} page Number of the page
   */
  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle sort change on table
   * @param {string} column Column of the table
   * @param {string} sortDirection Sort direction
   */
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle per rows (limit) change on table
   * @param {number} limit New limit value
   */
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/tax-rulesets/${row?.id}`));
  };

  /**
   * Handle search filter
   * @param {string} name Name of the field to search
   * @param {string} value Value of the input
   */
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach(
      (key) => (!filters[key] || filters[key] === '') && delete filters[key]
    );
    handleParams(filters);
  };

  return (
    <Layout title={i18n.t('tax_ruleset.title')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
              <Grid item>
                <Title title={i18n.t('tax_ruleset.title')} />
              </Grid>
              <Can I="create" a="tax_rules_set">
                <Grid item>
                  <Button
                    onClick={() => {
                      dispatch(PricingActions.resetCurrentRuleset());
                      dispatch(PricingActions.resetOnboarding());
                      history.push(buildUrl('/tax-rulesets/create'));
                    }}
                    style={{ textTransform: 'capitalize' }}
                    variant="contained"
                    color="primary"
                    data-cy="create-btn"
                  >
                    {i18n.t('common.create_ruleset')}
                  </Button>
                </Grid>
              </Can>
            </Grid>
          </Grid>
          <Grid item xs={3}>
            <SearchField
              label={i18n.t('ruleset.search')}
              field={{
                onChange: search,
                name: 'keyword',
                value: params.keyword,
              }}
              inputProps={{
                startAdornment: (
                  <InputAdornment
                    classes={{
                      root: classes.searchIcon,
                    }}
                    position="start"
                  >
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data={data}
                columns={columns}
                page={params.page}
                onSort={handleSort}
                limit={params.limit}
                loading={fetchingTaxRulesets}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                defaultSortAsc={!params?.sort_desc}
                defaultSortField={params?.sort_field}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
              />
            </Paper>
          </Grid>
        </Grid>
        <DeleteTaxRulesetConfirm />
      </Container>
    </Layout>
  );
};

export default TaxRulesetList;
